<template>
    <v-card class="elevation-0">
        <v-tabs
            centered
            grow
            color="primary"
            icons-and-text
            v-model="tab"
            class="pb-4"
        >
            <v-tab @click="goToTab('general')" href="#general" id="general">
                Informações Gerais
                <v-icon>mdi-information-outline</v-icon>
            </v-tab>
            <v-tab @click="goToTab('payment')" href="#payment" id="payment">
                Pagamento
                <v-icon>mdi-cash</v-icon>
            </v-tab>
            <v-tab @click="goToTab('facilities')" href="#facilities" id="facilities">
                Facilidades
                <v-icon>mdi-wifi</v-icon>
            </v-tab>
            <v-tab @click="goToTab('rooms')" href="#rooms" id="rooms">
                Quartos
                <v-icon>mdi-bed</v-icon>
            </v-tab>
            <v-tab @click="goToTab('images')" href="#images" id="images">
                Imagens
                <v-icon>mdi-camera</v-icon>
            </v-tab>
            <v-tab v-if="$can('manage.hotel-fare-update-tokens') && isEditingHotel" @click="goToTab('admin-actions')" href="#admin-actions" id="admin-actions">
                Ações Administrativas
                <v-icon>mdi-shield-account</v-icon>
            </v-tab>
            <v-tab v-if="$can('manage.hotel-unavailability-periods') && isEditingHotel" @click="goToTab('unavailability')" href="#unavailability" id="unavailability">
                Indisponibilidades
                <v-icon>mdi-calendar-remove</v-icon>
            </v-tab>
        </v-tabs>
        <v-container>
            <v-alert class="pa-2" v-if="wasUpdated" type="warning">
                Este hotel já sofreu alterações, <router-link :to="{name: 'hotelListChanges', params: {id: this.hotel.id}}"><u>clique aqui</u></router-link> para visualizá-las.
            </v-alert>
        </v-container>
        <div>
            <general-info
                v-on:goToTab="clickTab"
                v-show="tab === 'general'"
                ref="general"
                :hotel="hotel"
                :initial-filled-data="initialFilledData"
            />
            <payment
                v-on:goToTab="clickTab"
                v-show="tab === 'payment'"
                ref="payment"
                :hotel="hotel"
                :cnpj="getHotelCnpj()"
            />
            <facilities
                v-on:goToTab="clickTab"
                v-show="tab === 'facilities'"
                ref="facilities"
                :hotelAmenities="hotel !== null ? hotel.amenities : null"
            />
            <rooms
                v-on:goToTab="clickTab"
                v-show="tab === 'rooms'"
                ref="rooms"
                :hotelRooms="hotel !== null ? hotel.rooms : null"
            />
            <images
                v-on:finish="emitFinish"
                v-on:goToTab="clickTab"
                v-show="tab === 'images'"
                ref="images"
                :hotelThumb="hotel !== null ? hotel.thumbnail : null"
                :hotelImages="hotel !== null ? hotel.images : null"
                :hotelAgreementImage="hotel !== null ? hotel.agreement_image : null"
                :hotel-name="getHotelName()"
            />
            <admin-actions
                v-if="$can('manage.hotel-fare-update-tokens') && isEditingHotel"
                v-on:goToTab="clickTab"
                v-show="tab === 'admin-actions'"
                :hotel="hotel"
            />
            <unavailability-periods
                v-if="$can('manage.hotel-unavailability-periods') && isEditingHotel"
                v-show="tab === 'unavailability'"
                :hotel-id="hotel.id"
            />
        </div>

        <response-dialog
            :model="getResponseDialogData?.model"
            :type="getResponseDialogData?.dialogType"
            :message="getResponseDialogData?.dialogMessage"
            @close="closeModal"
        />

        <duplicated-hotels-dialog
            :model.sync="getDuplicatedDialogData.model"
            :hotels="getDuplicatedDialogData.hotels"
            @close="closeDuplicatedDialog"
        />
    </v-card>
</template>

<script>
import Facilities from "./HotelForm/Facilities";
import GeneralInfo from "./HotelForm/GeneralInfo";
import Images from "./HotelForm/Images";
import Rooms from "./HotelForm/Rooms";
import Payment from "./HotelForm/Payment.vue";
import {provide} from "vue";
import ResponseDialog from "../Dialog/ResponseDialog.vue";
import DuplicatedHotelsDialog from "../Dialog/DuplicatedHotelsDialog.vue";
import AdminActions from "./HotelForm/AdminActions.vue";
import UnavailabilityPeriods from "./HotelForm/UnavailabilityPeriods.vue";

export default {
    name: "HotelForm",
    components: {
        AdminActions,
        UnavailabilityPeriods,
        DuplicatedHotelsDialog,
        ResponseDialog,
        Payment,
        Facilities,
        GeneralInfo,
        Images,
        Rooms
    },
    props: {
        hotel: {
            type: Object,
            default: null
        },
        ownerCreationForm: {
            type: Boolean,
            default: false
        },
        dialogData: {
            type: Object,
            default: {
                loading: false,
                model: false,
                dialogTitle: '',
                dialogMessage: '',
                dialogType: ''
            }
        },
        duplicatedDialog: {
            type: Object,
            default: null,
        },
        initialFilledData: {
            type: Object,
            default: null,
        },
    },
    provide() {
        return {
            ownerCreationForm: this.ownerCreationForm
        }
    },
    data: () => ({
        tab: 'general',
        canGoToImages: false,
        dialog: false,
        wasUpdated: false,
    }),
    computed: {
        getResponseDialogData() {
            return this.dialogData;
        },
        getDuplicatedDialogData() {
            return {
                model: this.duplicatedDialog?.model ?? false,
                hotels: this.duplicatedDialog?.hotels ?? [],
            }
        },
        isEditingHotel () {
            return this.hotel !== null && (this.hotel?.name !== undefined && this.hotel?.name !== null);
        }
    },
    methods: {
        getHotelCnpj() {
            return this.$refs.general?.cnpj;
        },
        getHotelName() {
            return this.$refs.general?.hotelName?? ''
        },
        closeModal: function() {
          this.$emit('update:dialogData', { model: false });
          if (this.dialogData.dialogType === 'success' && !this.ownerCreationForm) {
              this.$router.push({ name: 'hotelList' });
          }
          if (this.dialogData.dialogType === 'success' && this.ownerCreationForm) {
              this.$router.push({ name: 'ownerCreationSuccess' });
          }
        },
        clickTab: function (tab) {
            document.getElementById(tab).click();
        },
        goToTab: function (tab) {
            this.tab = tab;
        },
        validate: function () {
            this.$refs.rooms.validate();
            if (!this.$refs.general.$data.valid) {
                this.$refs.general.validate()
                this.goToTab('general');
            } else if (!this.$refs.facilities.valid) {
                this.goToTab('facilities');
            } else if (!this.$refs.rooms.valid) {
                this.goToTab('rooms');
            } else if (!this.$refs.images.valid) {
                this.goToTab('images');
            } else {
                return true;
            }
        },
        emitFinish: async function () {
            if (!this.validate()) return;

            let data = this.$refs.general.getData();
            data.facilities = this.$refs.facilities.getData();
            data.rooms = this.$refs.rooms.getData();
            Object.assign(data, await this.$refs.images.getData());
            Object.assign(data, await this.$refs.payment.getData());

            this.$emit('update:dialogData', { model: false });
            this.$emit('finish', data);
        },
        closeDuplicatedDialog: function () {
            this.$emit('update:duplicatedDialog', {model: false, hotels: []});
        },
    },
    mounted() {
        if (this.hotel) {
            axios.get(`/api/hotel/list-changes/${this.hotel.id}?action=2`)
                .then(res => {
                    if (res.data.length !== 0) {
                        this.wasUpdated = true;
                    }
                });
        }
    }
}
</script>

<style scoped>
    .v-dialog .v-card__text {
        padding: 0 !important;
    }
    .v-dialog .v-alert {
        border-radius: 0 !important;
    }
    .v-dialog, .v-dialog .v-card__text {
        overflow: hidden !important;
    }
</style>
